// Carte Leaflet réelle du module AYA Chauffeur (marqueurs + itinéraire OSRM évolutifs).
import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Circle, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import useOSRMRoute from "../hooks/useOSRMRoute";

const pinIcon = (color) => L.divIcon({
  className: "",
  html: `<svg width="30" height="38" viewBox="0 0 30 38"><path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 23 15 23s15-12.5 15-23C30 6.7 23.3 0 15 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/><circle cx="15" cy="14.5" r="5.5" fill="#fff"/></svg>`,
  iconSize: [30, 38], iconAnchor: [15, 36],
});
const PIN_GREEN = pinIcon("#10B981");
const PIN_RED = pinIcon("#DC2626");
const CAR_ICON = L.divIcon({
  className: "",
  html: `<div style="width:34px;height:34px;border-radius:50%;background:#0B1F14;border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#34D399" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11"/><path d="M3 13a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3h-2m-14 0H3v-3z"/><circle cx="7.5" cy="16.5" r="1.8"/><circle cx="16.5" cy="16.5" r="1.8"/></svg></div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});
const BLUE_DOT = L.divIcon({
  className: "",
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#2563EB;border:3px solid #fff;box-shadow:0 0 0 7px rgba(37,99,235,0.28)"></div>',
  iconSize: [18, 18], iconAnchor: [9, 9],
});

function Fit({ points }) {
  const map = useMap();
  const key = JSON.stringify(points);
  useEffect(() => {
    if (points && points.length >= 2) map.fitBounds(L.latLngBounds(points), { padding: [34, 34] });
  }, [key, map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function ChauffeurMap({ from, to, color = "#10B981", showCar = false, carAt = null, progress = null, height = 190, radar = false, center = [5.336, -4.012], zoom = 12, testid = "chf-map", rounded = true }) {
  const { coords } = useOSRMRoute(from || null, to || null);
  const line = useMemo(() => {
    if (!from || !to) return null;
    return coords && coords.length > 1 ? coords.map(([lng, lat]) => [lat, lng]) : [from, to];
  }, [coords, from, to]);
  const carPos = useMemo(() => {
    if (!showCar) return null;
    if (carAt) return carAt;
    if (line && progress != null) {
      const i = Math.min(line.length - 1, Math.floor(progress * (line.length - 1)));
      return line[i];
    }
    return null;
  }, [showCar, carAt, line, progress]);

  return (
    <div className="chf-map" style={{ height, borderRadius: rounded ? 16 : 0, border: rounded ? undefined : "none" }} data-testid={testid}>
      <MapContainer center={center} zoom={zoom} zoomControl={false} attributionControl={false} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {radar && (
          <>
            <Circle center={center} radius={2200} pathOptions={{ color: "#10B981", fillColor: "#10B981", fillOpacity: 0.13, weight: 1.5 }} />
            <Marker position={center} icon={BLUE_DOT} />
          </>
        )}
        {line && <Polyline positions={line} pathOptions={{ color, weight: 5, opacity: 0.92 }} />}
        {from && <Marker position={from} icon={PIN_GREEN} />}
        {to && <Marker position={to} icon={PIN_RED} />}
        {carPos && <Marker position={carPos} icon={CAR_ICON} />}
        {from && to && <Fit points={[from, to]} />}
      </MapContainer>
    </div>
  );
}
